/**
 * HauntedPassiveSkillEffect — Astral oracle-path passive: post-projection
 * enemy hesitation debuff.
 *
 * After Astral Projection ends, every marked enemy receives a "haunted" debuff
 * lasting 8 seconds. While haunted, the enemy's attack wind-up is delayed by
 * 200ms — an involuntary flinch that buys the player a sliver of reaction time
 * on every swing.
 *
 * Integration points:
 *   1. `onProjectionReturn(markedEnemyIds)` — called by the game wiring layer
 *      when AstralProjectionSkillEffect deactivates (natural expiry or manual
 *      return). Receives the set of enemies marked during the projection.
 *      Applies the haunted debuff to each via StatusEffectSystem and begins
 *      internal timer tracking.
 *
 *   2. `getWindupDelay(enemyId)` — called by the enemy AI attack state machine
 *      before entering wind-up. Returns `markHesitationMs` (200ms) if the
 *      enemy is currently haunted, 0 otherwise. The caller adds this to its
 *      wind-up duration.
 *
 * Timer management: the passive tracks haunted durations internally (Map of
 * entityId → remaining ms) and ticks them down in `update(delta)`. On expiry,
 * the corresponding StatusEffectSystem entry is removed. This dual tracking
 * keeps the SkillEffect self-contained for simulation purposes while the
 * StatusEffectSystem integration provides the runtime hook for enemy AI to
 * query via StatusEffectTracker.has('haunted').
 *
 * Effect keys (from tree JSON):
 *   - `markHesitationMs`: 200 — delay added to each wind-up (ms)
 *   - `hesitationDurationMs`: 8000 — how long the haunted debuff lasts (ms)
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § astral_projection_passive
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HauntedPassiveDeps {
  /** Status effect integration — applies and removes the haunted debuff. */
  readonly statusEffects: {
    applyHaunted(enemyId: string, durationMs: number): void;
    removeHaunted(enemyId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface HauntedPassiveConfig {
  /** Delay added to each enemy wind-up while haunted (200ms). */
  readonly markHesitationMs: number;
  /** How long the haunted debuff lasts in ms (8000ms). */
  readonly hesitationDurationMs: number;
}

const DEFAULT_CONFIG: HauntedPassiveConfig = {
  markHesitationMs: 200,
  hesitationDurationMs: 8000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HauntedPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'astral_projection_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  readonly config: HauntedPassiveConfig;
  private readonly deps: HauntedPassiveDeps;

  /**
   * Internal timer tracking: enemyId → remaining haunted duration in ms.
   * Ticked down by update(). On expiry, the entry is removed and
   * deps.statusEffects.removeHaunted() is called.
   */
  private readonly _hauntedTimers = new Map<string, number>();

  constructor(deps: HauntedPassiveDeps, config: Partial<HauntedPassiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  /** Number of enemies currently carrying the haunted debuff. */
  get hauntedCount(): number { return this._hauntedTimers.size; }

  /** Whether a specific enemy is currently haunted. */
  isHaunted(enemyId: string): boolean { return this._hauntedTimers.has(enemyId); }

  /** Remaining haunted duration in ms for a specific enemy (0 if not haunted). */
  getRemaining(enemyId: string): number { return this._hauntedTimers.get(enemyId) ?? 0; }

  // ---------------------------------------------------------------------------
  // Integration API — called by external systems
  // ---------------------------------------------------------------------------

  /**
   * Called when Astral Projection deactivates (natural expiry or manual return).
   * Applies the haunted debuff to every enemy that was marked during projection.
   *
   * @param markedEnemyIds  Set of enemy entity IDs marked during the projection.
   */
  onProjectionReturn(markedEnemyIds: Iterable<string>): void {
    for (const enemyId of markedEnemyIds) {
      this._hauntedTimers.set(enemyId, this.config.hesitationDurationMs);
      this.deps.statusEffects.applyHaunted(enemyId, this.config.hesitationDurationMs);
    }
  }

  /**
   * Called by the enemy AI attack state machine before entering wind-up.
   *
   * Returns the hesitation delay (markHesitationMs, 200ms) if the enemy is
   * currently haunted, or 0 if they are not. The caller adds this to its
   * wind-up duration to implement the flinch.
   *
   * @param enemyId  The entity ID of the attacking enemy.
   */
  getWindupDelay(enemyId: string): number {
    return this._hauntedTimers.has(enemyId) ? this.config.markHesitationMs : 0;
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  /** Tick down haunted timers. Remove expired entries and notify StatusEffectSystem. */
  update(delta: number): void {
    for (const [enemyId, remaining] of this._hauntedTimers) {
      const next = Math.max(0, remaining - delta);
      if (next === 0) {
        this._hauntedTimers.delete(enemyId);
        this.deps.statusEffects.removeHaunted(enemyId);
      } else {
        this._hauntedTimers.set(enemyId, next);
      }
    }
  }

  /** Passives do not respond to player activation. */
  activate(): boolean { return false; }

  /** Active in the sense that haunted debuffs are currently ticking. */
  isActive(): boolean { return this._hauntedTimers.size > 0; }

  destroy(): void {
    this._hauntedTimers.clear();
  }
}
